import cornerstoneTools from "cornerstone-tools";
import {
  FLUID_SEGMENTATION_LABELS,
  FLUID_SEGMENTATION_DEFAULT_COLORS,
  getActiveFluidLabelsFromApi,
  mergeAdjacentFluidRowDatums,
  coalesceApiSegmentsToRowLines,
} from "./fluidManualBrush";
import { getFluidApiSegmentsForImage } from "../Viewers/utils";

/** @type {Map<string, Record<number, Record<string, number[][]>>>} */
const apiBaselinesByStack = new Map();

export function frameIndexFromImageId(imageId) {
  if (!imageId) return -1;
  const idStr = imageId.split("=").pop();
  const n = parseInt(idStr, 10);
  return Number.isFinite(n) ? n : -1;
}

function resolveActiveLabels(fluidSegmentation) {
  return getActiveFluidLabelsFromApi(fluidSegmentation);
}

function deepCloneSegments(byLabel, activeLabels = FLUID_SEGMENTATION_LABELS) {
  const out = {};
  activeLabels.forEach((label) => {
    out[label] = (byLabel[label] || []).map((seg) => [...seg]);
  });
  return out;
}

/** Store per-frame API fluid baseline after initial load (for reset). */
export function snapshotFluidBaselinesForStack(
  stackId,
  imageIds,
  activeLabels = FLUID_SEGMENTATION_LABELS
) {
  if (!stackId || !Array.isArray(imageIds)) return;
  const map = {};
  imageIds.forEach((imageId, frameIndex) => {
    const live = getFluidApiSegmentsForImage(imageId);
    map[frameIndex] = deepCloneSegments(live, activeLabels);
  });
  apiBaselinesByStack.set(stackId, map);
}

export function getFluidBaselineForFrame(stackId, frameIndex) {
  return apiBaselinesByStack.get(stackId)?.[frameIndex] || null;
}

function apiSegmentsToDatums(
  segmentsByLabel,
  fluidVisibility,
  fluidColors,
  activeLabels = FLUID_SEGMENTATION_LABELS
) {
  const datums = [];
  activeLabels.forEach((label) => {
    const segments = segmentsByLabel[label] || [];
    const color =
      fluidColors?.[label] || FLUID_SEGMENTATION_DEFAULT_COLORS[label];
    const visible =
      fluidVisibility && typeof fluidVisibility[label] === "boolean"
        ? fluidVisibility[label]
        : true;
    segments.forEach(([x1, x2, y]) => {
      datums.push({
        visible,
        active: true,
        color,
        label,
        invalidated: true,
        alpha: 0.35,
        start: { x: Number(x1), y: Number(y) },
        end: { x: Number(x2), y: Number(y) },
      });
    });
  });
  return mergeAdjacentFluidRowDatums(datums);
}

/** Restore one OPT frame to its API baseline fluid lines. */
export function resetFluidFrameToBaseline(
  stackId,
  frameIndex,
  imageId,
  fluidVisibility,
  fluidColors
) {
  const baseline = getFluidBaselineForFrame(stackId, frameIndex);
  if (!baseline || !imageId) return false;

  const mgr = cornerstoneTools.globalImageIdSpecificToolStateManager;
  if (!mgr?.saveImageIdToolState || !mgr?.restoreImageIdToolState) {
    return false;
  }

  const datums = apiSegmentsToDatums(baseline, fluidVisibility, fluidColors);
  const bundle = mgr.saveImageIdToolState(imageId) || {};
  bundle.FluidSegmentation = { data: datums };
  mgr.restoreImageIdToolState(imageId, bundle);
  return true;
}

function apiSegmentToInternalTriple(seg) {
  if (!Array.isArray(seg)) return null;
  if (seg.length === 3) {
    const x1 = Number(seg[0]);
    const x2 = Number(seg[1]);
    const y = Number(seg[2]);
    if (Number.isFinite(x1) && Number.isFinite(x2) && Number.isFinite(y)) {
      return [x1, x2, y];
    }
  }
  if (
    seg.length === 2 &&
    Array.isArray(seg[0]) &&
    Array.isArray(seg[1]) &&
    seg[0].length === 2 &&
    seg[1].length === 2
  ) {
    const x1 = Number(seg[0][0]);
    const y = Number(seg[0][1]);
    const x2 = Number(seg[1][0]);
    if (Number.isFinite(x1) && Number.isFinite(x2) && Number.isFinite(y)) {
      return [x1, x2, y];
    }
  }
  return null;
}

function bscansHaveFluidData(bscans, activeLabels = FLUID_SEGMENTATION_LABELS) {
  if (!Array.isArray(bscans)) return false;
  return bscans.some((bscan) =>
    activeLabels.some((label) => (bscan[label] || []).length > 0)
  );
}

/** Build `bscans` from raw API fluid segmentation when tool state is not ready yet. */
export function buildFluidBscansFromApiFluidData(imageIds, fluidSegmentation) {
  if (!Array.isArray(imageIds) || !fluidSegmentation) return [];
  const activeLabels = resolveActiveLabels(fluidSegmentation);
  const apiBscans =
    fluidSegmentation?.bscans || fluidSegmentation?.result?.bscans || [];

  return imageIds.map((imageId, frameIndex) => {
    const bscanId = frameIndex + 1;
    const apiBscan = apiBscans.find((b) => Number(b?.bscan_id) === bscanId);
    const entry = { bscan_id: bscanId };
    activeLabels.forEach((label) => {
      const segments = Array.isArray(apiBscan?.[label]) ? apiBscan[label] : [];
      entry[label] = segments
        .map((seg) => apiSegmentToInternalTriple(seg))
        .filter(Boolean);
    });
    return entry;
  });
}

/** Build `bscans` with internal `[x1,x2,y]` segments (tool state / reset baselines). */
export function buildFluidBscansFromToolState(
  imageIds,
  activeLabels = FLUID_SEGMENTATION_LABELS
) {
  if (!Array.isArray(imageIds)) return [];
  return imageIds.map((imageId, frameIndex) => {
    const live = getFluidApiSegmentsForImage(imageId);
    const entry = { bscan_id: frameIndex + 1 };
    activeLabels.forEach((label) => {
      entry[label] = live[label] ? live[label].map((s) => [...s]) : [];
    });
    return entry;
  });
}

function normalizeBscanSegmentTriple(seg) {
  if (!Array.isArray(seg)) return null;
  if (seg.length === 3) {
    const x1 = Number(seg[0]);
    const x2 = Number(seg[1]);
    const y = Number(seg[2]);
    if (Number.isFinite(x1) && Number.isFinite(x2) && Number.isFinite(y)) {
      return [x1, x2, y];
    }
  }
  if (
    seg.length === 2 &&
    Array.isArray(seg[0]) &&
    Array.isArray(seg[1]) &&
    seg[0].length === 2 &&
    seg[1].length === 2
  ) {
    const x1 = Number(seg[0][0]);
    const x2 = Number(seg[1][0]);
    const y = Number(seg[0][1]);
    if (Number.isFinite(x1) && Number.isFinite(x2) && Number.isFinite(y)) {
      return [x1, x2, y];
    }
  }
  return null;
}

export function convertBscanForVolumesApi(
  bscan,
  activeLabels = FLUID_SEGMENTATION_LABELS
) {
  const entry = { bscan_id: bscan.bscan_id };
  activeLabels.forEach((label) => {
    const raw = (bscan[label] || [])
      .map((seg) => normalizeBscanSegmentTriple(seg))
      .filter(Boolean);
    entry[label] = coalesceApiSegmentsToRowLines(raw);
  });
  return entry;
}

/** `bscans` array for POST /compute_volumes (`fda-payload-working.json` uses `[x1,x2,y]` for all fluids). */
export function buildFluidBscansForVolumesApi(
  imageIds,
  fluidSegmentation = null,
  activeLabelsOverride = null
) {
  const activeLabels = activeLabelsOverride || resolveActiveLabels(fluidSegmentation);
  const fromTool = buildFluidBscansFromToolState(imageIds, activeLabels);
  const source = bscansHaveFluidData(fromTool, activeLabels)
    ? fromTool
    : fluidSegmentation
      ? buildFluidBscansFromApiFluidData(imageIds, fluidSegmentation)
      : fromTool;
  return source.map((bscan) => convertBscanForVolumesApi(bscan, activeLabels));
}

const PAYLOAD_POINT_COLORS = {
  IRF: "#ff0000",
  PED: "#0000ff",
  SRF: "#00ff00",
  SHRM: "#ffff00",
};

/** Endpoint markers for the current frame's volume-API payload (debug overlay). */
export function getFluidPayloadEndpointsForFrame(
  imageIds,
  frameIndex,
  fluidSegmentation = null
) {
  if (!Array.isArray(imageIds) || frameIndex < 0 || frameIndex >= imageIds.length) {
    return [];
  }

  const activeLabels = resolveActiveLabels(fluidSegmentation);
  const fromTool = buildFluidBscansFromToolState(imageIds, activeLabels);
  const source = bscansHaveFluidData(fromTool, activeLabels)
    ? fromTool
    : fluidSegmentation
      ? buildFluidBscansFromApiFluidData(imageIds, fluidSegmentation)
      : fromTool;
  const bscan = source[frameIndex];
  if (!bscan) return [];

  const points = [];
  activeLabels.forEach((label) => {
    (bscan[label] || []).forEach((seg) => {
      if (!Array.isArray(seg) || seg.length !== 3) return;
      const x1 = Number(seg[0]);
      const x2 = Number(seg[1]);
      const y = Number(seg[2]);
      if (!Number.isFinite(x1) || !Number.isFinite(x2) || !Number.isFinite(y)) {
        return;
      }
      const color = PAYLOAD_POINT_COLORS[label] || "#ffffff";
      points.push({ x: x1, y, label, color, end: "start" });
      points.push({ x: x2, y, label, color, end: "end" });
    });
  });
  return points;
}

/** Compare live frame fluid vs baseline — true if user edited this frame. */
export function isFluidFrameDirty(stackId, frameIndex, imageId) {
  const baseline = getFluidBaselineForFrame(stackId, frameIndex);
  if (!baseline || !imageId) return false;
  const live = getFluidApiSegmentsForImage(imageId);
  return JSON.stringify(live) !== JSON.stringify(baseline);
}
